import { Skeleton, Typography } from "@mui/material";
import classnames from "classnames";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useTranslation from "../../hooks/useTranslation";
import TrainingModule from "../../models/trainingModule";
import { TrainingAudience } from "../../types/training";
import { TrainingModuleOverview } from "../TrainingModuleOverview/TrainingModuleOverview";
import { Card } from "../ui/Card/Card";
import { Chip } from "../ui/Chip/Chip";
import { Modal } from "../ui/Modal/Modal";
import { IconTypes } from "../ui/icons";
import { Icon } from "../ui/icons/Icon";
import "./TrainingModuleCard.scss";

interface TrainingModuleProps {
  trainingModule: TrainingModule;
  isLoading?: boolean;
  isActive?: boolean;
}

export const TrainingModuleCard = ({
  trainingModule,
  isLoading = false,
}: TrainingModuleProps) => {
  const { t } = useTranslation("trainingPage");
  const [isModuleOpen, setIsModuleOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const moduleReferenceId = searchParams.get("module");

  useEffect(() => {
    if (moduleReferenceId && moduleReferenceId === trainingModule.referenceId) {
      setIsModuleOpen(true);
    }
  }, [moduleReferenceId, trainingModule.referenceId]);

  const buttonClasses = classnames(
    "TrainingModuleCard__innerCard__content__button",
    {
      "TrainingModuleCard__innerCard__content__button--disabled":
        !trainingModule.isAccessible(),
    }
  );

  const handleCloseModal = () => {
    setIsModuleOpen(false);
    if (searchParams.has("module")) {
      searchParams.delete("module");
      setSearchParams(searchParams);
    }
  };

  const cardClasses = classnames("TrainingModuleCard__innerCard", {
    "TrainingModuleCard__innerCard--disabled": !trainingModule.isAccessible(),
  });

  const renderLoading = () => {
    return (
      <>
        <div className="TrainingModuleCard__innerCard__header">
          <div className="TrainingModuleCard__innerCard__header__pill"></div>
        </div>
        <div className="TrainingModuleCard__innerCard__content">
          <Skeleton
            width={"100%"}
            height={"100%"}
            style={{ borderRadius: "16px" }}
          />
        </div>
      </>
    );
  };

  return (
    <div className="TrainingModuleCard">
      <Card containerClassName={cardClasses}>
        {!isLoading && (
          <>
            <div className="TrainingModuleCard__innerCard__header">
              <img
                className="TrainingModuleCard__innerCard__header__img"
                src={trainingModule.gradient}
                alt=""
              />
              <div className="TrainingModuleCard__innerCard__header__pill">
                <Icon
                  type={
                    trainingModule.audience === TrainingAudience.All
                      ? IconTypes.ManyPeople
                      : IconTypes.ChatPerson
                  }
                />
                <Typography>
                  {t(
                    `modules.audience.${trainingModule.audience.toLowerCase()}`
                  )}
                </Typography>
              </div>
            </div>
            <div className="TrainingModuleCard__innerCard__content">
              <div>
                <Typography
                  variant="h3"
                  className="TrainingModuleCard__innerCard__content__title"
                >
                  {trainingModule.title}
                </Typography>
                <div className="TrainingModuleCard__innerCard__content__tag">
                  <Icon type={IconTypes.GraduationHat} />
                  <Typography>Gender Pronouns</Typography>
                </div>
              </div>
              <Typography className="TrainingModuleCard__innerCard__content__description">
                {trainingModule.description}
              </Typography>
              {trainingModule.isAccessible() && (
                <button
                  className={buttonClasses}
                  onClick={() => setIsModuleOpen(true)}
                  title={`Open course ${trainingModule.title}`}
                >
                  <Icon type={IconTypes.RedArrow} />
                </button>
              )}
              {trainingModule.isComingSoon() && (
                <Chip variant="outlined" label={t("modules.comingSoon")} />
              )}
              {trainingModule.isNotAvailable() && (
                <Chip variant="outlined" label={t("modules.notAvailable")} />
              )}
            </div>
          </>
        )}
        {isLoading && renderLoading()}
      </Card>

      <Modal
        open={isModuleOpen}
        onClose={() => {
          handleCloseModal();
        }}
        light
        className="TrainingModuleCard__modal"
      >
        <TrainingModuleOverview trainingModule={trainingModule} />
      </Modal>
    </div>
  );
};
